import { useMutation, useQueryClient } from '@tanstack/react-query';
import PatientService from '@/services/PatientService';
import type { ProgramNote } from '@/services/PatientService';
import { PATIENT_KEYS } from './keys';

export interface CreateNoteVars {
  userid: string | number;
  message: string;
  authorid: string | number;
}

export function useCreatePatientNote() {
  const qc = useQueryClient();
  return useMutation<ProgramNote, Error, CreateNoteVars>({
    mutationFn: (vars) => PatientService.createNote(vars.userid, vars.message, vars.authorid),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PATIENT_KEYS.notes(vars.userid) });
    },
  });
}

export interface UpdateNoteVars {
  noteid: string | number;
  userid: string | number;
  message: string;
  authorid: string | number;
}

export function useUpdatePatientNote() {
  const qc = useQueryClient();
  return useMutation<ProgramNote, Error, UpdateNoteVars>({
    mutationFn: (vars) => PatientService.updateNote(vars.noteid, vars.userid, vars.message, vars.authorid),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PATIENT_KEYS.notes(vars.userid) });
    },
  });
}

export interface DeleteNoteVars {
  noteid: string | number;
  userid: string | number;
}

export function useDeletePatientNote() {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteNoteVars>({
    mutationFn: (vars) => PatientService.deleteNote(vars.noteid, vars.userid),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PATIENT_KEYS.notes(vars.userid) });
    },
  });
}

export interface UpdatePatientCategoryVars {
  userid: string | number;
  category?: number;
}

// Program-side write, no query to invalidate — measures only record from here, surfaced by the BP chart on the patient's next reads.
export function useUpdatePatientCategory() {
  return useMutation<void, Error, UpdatePatientCategoryVars>({
    mutationFn: (vars) => PatientService.updatePatientCategory(vars.userid, vars.category ?? 0),
  });
}

// Exact: PATIENT_KEYS.byId prefixes every other per-patient key, and only getpatient's category changes.
// Returned, so mutateAsync resolves only once the fresh category is read.
function refreshCategory(qc: ReturnType<typeof useQueryClient>, userid: string | number) {
  return qc.invalidateQueries({ queryKey: PATIENT_KEYS.byId(userid), exact: true });
}

export function useArchivePatient() {
  const qc = useQueryClient();
  return useMutation<void, Error, { userid: string | number }>({
    mutationFn: (vars) => PatientService.archivePatient(vars.userid),
    onSettled: (_d, _e, vars) => refreshCategory(qc, vars.userid),
  });
}

export function useUnarchivePatient() {
  const qc = useQueryClient();
  return useMutation<void, Error, { userid: string | number }>({
    mutationFn: (vars) => PatientService.unarchivePatient(vars.userid),
    onSettled: (_d, _e, vars) => refreshCategory(qc, vars.userid),
  });
}

export type { ProgramNote };
